import { buildDimensionByQuizIdQueryKey } from "@/hooks/dimension-by-quiz-id.hook";
import { buildResponseByIdQueryKey } from "@/hooks/response-by-id.hook";
import { buildTopicByIdQueryKey } from "@/hooks/topic-by.id.hook";
import {
  Choice,
  ChoiceType,
  DimensionId,
  TopicWithRefreshStatus,
} from "@/types/questionnaries";
import { QueryClient } from "@tanstack/react-query";

/**
 * Recursively cleans a choice tree.
 *
 */
export const cleanChoice = (choice: Choice): Choice => {
  if (choice.type === ChoiceType.Composite) {
    return {
      ...choice,
      selected: false,
      choices: choice.choices.map(cleanChoice),
    };
  }

  if (choice.type === ChoiceType.Fillable) {
    return {
      ...choice,
      selected: false,
      answerValue: "",
    };
  }

  if (choice.type === ChoiceType.Selectable) {
    return {
      ...choice,
      selected: false,
    };
  }

  return choice;
};

/**
 * Finds a choice by its ID and replaces it with a new choice.
 *
 */
export const findAndReplaceById = (
  choices: Choice[],
  id: string,
  newChoice: Choice,
): Choice[] => {
  return choices.map((node) => {
    if (node.id === id) {
      // At this point, the node has been found, but if the choice
      // has not been selected, its children must me cleaned too.
      if (newChoice.type === ChoiceType.Composite) {
        if (!newChoice.selected) {
          return {
            ...newChoice,
            choices: newChoice.choices.map(cleanChoice),
          };
        }
      }

      return newChoice;
    }

    if (node.type === ChoiceType.Composite) {
      return {
        ...node,
        choices: findAndReplaceById(node.choices, id, newChoice),
      };
    }

    return node;
  });
};

/**
 * Checks if a multiple choice has been selected, but not any of its children.
 *
 */
export const validateSelection = (node: Choice): boolean => {
  if (node.type === ChoiceType.Composite) {
    if (node.selected && node.choices.every((child) => !child.selected)) {
      return false;
    }

    return node.choices.every(validateSelection);
  }

  return true;
};

export const refetchTopicAndQuestionDetails = (variables: {
  queryClient: QueryClient;
  companyId: string;
  quizId: string;
  responseId: string;
  dimension: DimensionId;
  topic: string;
}) => {
  const { queryClient } = variables;

  // Refetch the question details
  queryClient.invalidateQueries({
    queryKey: buildResponseByIdQueryKey({
      companyId: variables.companyId,
      quizId: variables.quizId,
      responseId: variables.responseId,
    }),
  });

  // Get the topic and the dimension we are working
  const topicQueryKey = buildTopicByIdQueryKey({
    companyId: variables.companyId,
    applicationQuizId: variables.quizId,
    dimension: variables.dimension,
    topicId: variables.topic,
  });

  // Update the topic and dimension summary to indicate that
  // it needs to be refreshed
  queryClient.setQueryData(
    topicQueryKey,
    (oldData: TopicWithRefreshStatus) => ({
      ...oldData,
      needsRefresh: true,
    }),
  );

  // Backend has a delay of 5s to update the dimension summary (and
  // therefore the topic summary) after a response is saved.
  // Use 10 segs to avoid a race condition
  setTimeout(() => {
    // Refetch the topic
    queryClient.refetchQueries({
      queryKey: topicQueryKey,
    });

    // Refetch the dimension summary
    queryClient.refetchQueries({
      queryKey: buildDimensionByQuizIdQueryKey({
        companyId: variables.companyId,
        applicationQuizId: variables.quizId,
        dimension: variables.dimension,
      }),
    });

    // Clear the quiz details
    queryClient.invalidateQueries({
      queryKey: [
        "companies",
        variables.companyId,
        "quiz",
        variables.quizId,
        "details",
      ],
    });
  }, 1000 * 10);
};
